'use client'

import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import type { Group, TripType, GroupStatus, GroupRetention } from '@/features/stm/lib/types'
import {
  TRIP_TYPES,
  TRIP_TYPE_UNSET,
  GROUP_STATUSES,
  GROUP_RETENTION_OPTIONS,
} from '@/features/stm/lib/types'
import { ChurchOrgCombobox } from '@/features/stm/components/forms/ChurchOrgCombobox'
import {
  DateRangePickerControl,
  formatLocalDateYmd,
  parseYmdToLocalDate,
} from '@/shared/components/form-inputs/date-range-picker-control'
import { cn } from '@/shared/lib/utils'

type GroupOverallStatusCardProps = {
  group: Group
  groupName: string
  churchOrg: string
  projectId: string
  tripType: TripType | null
  participants: string
  registeredParticipants: number
  startDate: string
  endDate: string
  status: GroupStatus
  retention: GroupRetention | null
  onGroupNameChange: (v: string) => void
  onChurchOrgChange: (v: string) => void
  onProjectIdChange: (v: string) => void
  onTripTypeChange: (v: TripType | null) => void
  onParticipantsChange: (v: string) => void
  onStartDateChange: (v: string) => void
  onEndDateChange: (v: string) => void
  onStatusChange: (v: GroupStatus) => void
  onRetentionChange: (v: GroupRetention | null) => void
  /** When true (e.g. Group Leader view), all fields are read-only */
  readOnly?: boolean
}

export function GroupOverallStatusCard({
  group,
  groupName,
  churchOrg,
  projectId,
  tripType,
  participants,
  registeredParticipants,
  startDate,
  endDate,
  status,
  onGroupNameChange,
  onChurchOrgChange,
  onProjectIdChange,
  onTripTypeChange,
  onParticipantsChange,
  onStartDateChange,
  onEndDateChange,
  onStatusChange,
  retention,
  onRetentionChange,
  readOnly = false,
}: GroupOverallStatusCardProps) {
  const inputClass = readOnly ? 'bg-muted cursor-not-allowed' : ''
  return (
    <div className="space-y-4 pt-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="overall-group-name">Group Name *</Label>
          <Input
            id="overall-group-name"
            placeholder="First Baptist Youth Group"
            value={groupName}
            onChange={(e) => onGroupNameChange((e.target as HTMLInputElement).value)}
            disabled={readOnly}
            readOnly={readOnly}
            className={inputClass}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="overall-project-id">Project ID</Label>
          <Input
            id="overall-project-id"
            placeholder="e.g. PRJ-2026-001"
            value={projectId}
            onChange={(e) => onProjectIdChange((e.target as HTMLInputElement).value)}
            disabled={readOnly}
            readOnly={readOnly}
            className={inputClass}
          />
        </div>
        {group.sr_event_id != null && (
          <div className="space-y-2">
            <Label htmlFor="overall-sr-event-id">Service Reef Event ID</Label>
            <Input
              id="overall-sr-event-id"
              value={String(group.sr_event_id)}
              readOnly
              disabled
              className="bg-muted cursor-not-allowed"
            />
          </div>
        )}
        <div className="space-y-2 sm:col-span-2 lg:col-span-1">
          <Label htmlFor="overall-church-org">Church/Organization/Individual *</Label>
          <ChurchOrgCombobox
            value={churchOrg}
            onChange={onChurchOrgChange}
            placeholder="Type or select church/organization"
            disabled={readOnly}
          />
        </div>
        <div className="space-y-2 sm:col-span-2 lg:col-span-1">
          <DateRangePickerControl
            id="overall-trip-dates"
            label="Trip Dates (start and end) *"
            disabled={readOnly}
            value={{
              from: parseYmdToLocalDate(startDate),
              to: parseYmdToLocalDate(endDate),
            }}
            onChange={(v) => {
              onStartDateChange(v.from ? formatLocalDateYmd(v.from) : '')
              onEndDateChange(v.to ? formatLocalDateYmd(v.to) : '')
            }}
          />
        </div>
        <div className="space-y-2">
          <Label>Trip type</Label>
          <Select
            value={tripType ?? TRIP_TYPE_UNSET}
            onValueChange={(v) =>
              onTripTypeChange(v === TRIP_TYPE_UNSET ? null : (v as TripType))
            }
            disabled={readOnly}
          >
            <SelectTrigger className={inputClass}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TRIP_TYPE_UNSET}>Not set</SelectItem>
              {TRIP_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="overall-participants">Expected Participants *</Label>
            <Input
              id="overall-participants"
              type="number"
              min={0}
              value={participants}
              onChange={(e) => onParticipantsChange((e.target as HTMLInputElement).value)}
              disabled={readOnly}
              readOnly={readOnly}
              className={inputClass}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="registered-participants">Registered Participants</Label>
            <Input
              id="registered-participants"
              value={registeredParticipants.toLocaleString()}
              readOnly
              disabled
              className="bg-muted cursor-not-allowed"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Status *</Label>
          <Select value={status} onValueChange={(v) => onStatusChange(v as GroupStatus)} disabled={readOnly}>
            <SelectTrigger className={inputClass}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GROUP_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>New / Return *</Label>
          <Select
            value={retention === 'New' || retention === 'Return' ? retention : ''}
            onValueChange={(v) => onRetentionChange(v === '' ? null : (v as GroupRetention))}
            disabled={readOnly}
          >
            <SelectTrigger className={cn(inputClass, '[&[data-placeholder]]:text-muted-foreground')}>
              <SelectValue placeholder="Select New or Return" />
            </SelectTrigger>
            <SelectContent>
              {GROUP_RETENTION_OPTIONS.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {group.leader_name && (
        <div className="space-y-1">
          <Label className="text-muted-foreground">Mission Trip Leader</Label>
          <Input
            value={group.leader_name}
            readOnly
            disabled
            className="bg-muted cursor-not-allowed"
          />
        </div>
      )}
    </div>
  )
}
